import { useEffect, useRef, useState } from "react";
import { marked } from "marked";
import mermaid from "mermaid";

mermaid.initialize({
  startOnLoad: false,
  theme: "base",
  themeVariables: {
    primaryColor: "#FFF0EA",
    primaryTextColor: "#1A1A1A",
    primaryBorderColor: "#E8571A",
    lineColor: "#4A5568",
    secondaryColor: "#E8F5EF",
    tertiaryColor: "#EFF6FF",
    fontSize: "13px",
    fontFamily: "Inter, sans-serif",
  },
  securityLevel: "loose",
});

marked.setOptions({
  gfm: true,
  breaks: true,
});

function MermaidBlock({ chartCode }) {
  const containerRef = useRef(null);
  const [svgHtml, setSvgHtml] = useState("");
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;
    const renderChart = async () => {
      try {
        const id = `mermaid-${Math.random().toString(36).substring(2, 9)}`;
        const cleanChart = chartCode
          .trim()
          .replace(/&gt;/g, ">")
          .replace(/&lt;/g, "<")
          .replace(/&amp;/g, "&");
        const { svg } = await mermaid.render(id, cleanChart);
        if (isMounted) {
          setSvgHtml(svg);
          setError(null);
        }
      } catch (err) {
        if (isMounted) {
          console.warn("Mermaid render error:", err);
          setError("Diagram preview unavailable");
        }
      }
    };
    renderChart();
    return () => {
      isMounted = false;
    };
  }, [chartCode]);

  if (error) {
    return (
      <div className="diagram-fallback">
        <div className="diagram-badge">📊 Flowchart / Process Diagram</div>
        <pre className="raw-diagram-code">{chartCode}</pre>
      </div>
    );
  }

  return (
    <div className="diagram-container">
      <div className="diagram-badge">📊 Process Flow &amp; Timeline Diagram</div>
      <div
        ref={containerRef}
        className="mermaid-svg-wrapper"
        dangerouslySetInnerHTML={{ __html: svgHtml }}
      />
    </div>
  );
}

// Visual diagram card component for architectural blueprints & illustrations
function VisualDiagramCard({ src, title, description, onZoom }) {
  return (
    <div className="visual-image-card">
      <div className="visual-card-header">
        <span className="visual-card-icon">📐</span>
        <span className="visual-card-title">{title || "Construction Schematic & Plan"}</span>
        <button className="zoom-btn" onClick={() => onZoom(src, title)} title="Click to zoom">
          🔍 Enlarge (Full View)
        </button>
      </div>
      <div className="visual-card-img-wrap" onClick={() => onZoom(src, title)}>
        <img src={src} alt={title || "Construction Diagram"} loading="lazy" />
      </div>
      {description && <div className="visual-card-desc">{description}</div>}
    </div>
  );
}

export default function MarkdownRenderer({ content, onQuickAction }) {
  const [modalImage, setModalImage] = useState(null);

  if (!content) return null;

  const handleZoom = (src, title) => {
    setModalImage({ src, title });
  };

  const closeModal = () => {
    setModalImage(null);
  };

  // Determine relevant contextual diagram if none is explicitly in markdown
  const lower = content.toLowerCase();
  const explicitImage = content.includes("![](") || content.includes("![") || content.includes("<img");
  
  let contextualDiagram = null;
  if (!explicitImage) {
    if (lower.includes("vastu") || lower.includes("ishaanya") || lower.includes("nairutya") || lower.includes("dishayein") || lower.includes("pooja room")) {
      contextualDiagram = {
        src: "/diagrams/vastu-layout.svg",
        title: "Vastu Shastra Residential 8-Direction Chart",
        description: "Standard Indian Vastu guidelines for main rooms, entry points and water storage."
      };
    } else if (lower.includes("foundation") || lower.includes("footing") || lower.includes("black cotton") || lower.includes("soil") || lower.includes("pile")) {
      contextualDiagram = {
        src: "/diagrams/foundation-types.svg",
        title: "Foundation Selection & Soil Bearing Capacity Chart",
        description: "Pad Footing vs Raft vs Under-Reamed Piles based on IS 2911 and site soil."
      };
    } else if (lower.includes("column") || lower.includes("beam") || lower.includes("slab") || lower.includes("rcc") || lower.includes("is 456") || lower.includes("stirrup") || lower.includes("reinforcement")) {
      contextualDiagram = {
        src: "/diagrams/rcc-column-detail.svg",
        title: "IS 456 RCC Column Reinforcement & Stirrups Detailing",
        description: "Cross-section, main rebars (12/16mm Fe500D), 40mm cover and 135° seismic hooks."
      };
    } else if (lower.includes("stage") || lower.includes("step") || lower.includes("phase") || lower.includes("timeline") || lower.includes("mahina") || lower.includes("month")) {
      contextualDiagram = {
        src: "/diagrams/construction-stages.svg",
        title: "House Construction Lifecycle (6 Phases from Approval to Handover)",
        description: "Sequential month-by-month stages with budget percentage allocations."
      };
    } else if (lower.includes("cost") || lower.includes("kharcha") || lower.includes("rate") || lower.includes("material") || lower.includes("budget") || lower.includes("lakh")) {
      contextualDiagram = {
        src: "/diagrams/cost-distribution.svg",
        title: "Indian Residential Construction Cost Breakdown & Material Share",
        description: "Cement 16%, Steel 14%, Labour 28%, Finishing 20%, MEP 10% benchmark distribution."
      };
    }
  }

  // Parse markdown and replace markdown image links ![title](url) with VisualDiagramCard
  const parts = [];
  const mermaidRegex = /```mermaid\s*([\s\S]*?)```/g;
  let lastIndex = 0;
  let match;

  while ((match = mermaidRegex.exec(content)) !== null) {
    if (match.index > lastIndex) {
      parts.push({
        type: "markdown",
        content: content.slice(lastIndex, match.index),
      });
    }
    parts.push({
      type: "mermaid",
      content: match[1].trim(),
    });
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < content.length) {
    parts.push({
      type: "markdown",
      content: content.slice(lastIndex),
    });
  }

  return (
    <div className="rich-markdown-content">
      {parts.map((part, index) => {
        if (part.type === "mermaid") {
          return <MermaidBlock key={`mermaid-${index}`} chartCode={part.content} />;
        }
        const html = marked.parse(part.content);
        return (
          <div
            key={`md-${index}`}
            className="md-html-chunk"
            dangerouslySetInnerHTML={{ __html: html }}
          />
        );
      })}

      {contextualDiagram && (
        <VisualDiagramCard
          src={contextualDiagram.src}
          title={contextualDiagram.title}
          description={contextualDiagram.description}
          onZoom={handleZoom}
        />
      )}

      {/* Lightbox / Zoom Modal */}
      {modalImage && (
        <div className="diagram-modal-overlay" onClick={closeModal}>
          <div className="diagram-modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="diagram-modal-header">
              <h3>{modalImage.title || "Full Resolution Construction Diagram"}</h3>
              <button className="modal-close-btn" onClick={closeModal}>✕ Close</button>
            </div>
            <div className="diagram-modal-body">
              <img src={modalImage.src} alt={modalImage.title} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
